import { Component, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { of, Subscription, throwError } from 'rxjs';
import { filter, first, mergeMap, tap } from 'rxjs/operators';
import { DisbursementSummaryContext, DisbursementSummaryModal } from '../../finance/disbursement/summary/disbursement-summary-modal.component';
import { FinanceRoutes } from '../../finance/finance-route-lookups';
import { ReceiptService } from '../../finance/services/receipt.service';
import { BusinessTransactionType } from '../../foundation/models/enums/business-transaction-type.enum';
import { BaseComponent } from '../../shared/base-components/base.component';
import { ValidationResultModel } from '../../shared/models/validation-results/validation-result.model';
import { ValidationStateModel } from '../../shared/models/validation-results/validation-state.model';
import { ValidationModel } from '../../shared/models/validation-results/validation.model';
import { LocationService } from '../../shared/services/location/location.service';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { FileSaverService } from '../../shared/services/output/file-saver.service';
import { ControlValidationMessagesService } from '../../shared/validators/control-validation-messages.service';
import { CustomerAccountTransactionSearchCriteria } from '../models/customer-account-transaction-search-criteria.model';
import { CustomerAccountService } from './service/customer-account.service';
import { getCurrentDate, setServerDate } from '../../shared/helpers/time-helpers.model';
import * as moment from 'moment';
import { CustomerAccountServiceHistoryViewModel } from '../models/views/customer-account-service-history-view.model';
import { downloadJsonToCsv } from '../../shared/helpers/json-to-csv-converter';

@Component({
    selector: 'customer-account-transaction-search',
    templateUrl: './customer-account-transaction-search.html',
    inputs: ['customerAccountNumberId']
})
export class CustomerAccountTransactionSearchComponent extends BaseComponent {
    public customerAccountNumberId: number;
    public customerAccountType: string;
    public form: UntypedFormGroup;
    public inProgress: boolean;
    public searchResults;
    public FinanceRoutes = FinanceRoutes;
    public rows = 10;
    public dataStore: any = [];
    private previousOrder: number;
    private previousSort = '';
    private previousPageNumber = -1;
    public totalRecords: number;

    // The inputs that the form is loaded w. Used for resetting it.
    private formInitialValue: {[key: string]: any};

    /**
    * Enum for all business transaction types.
    */
    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();

    // Gets or sets the business unit list
    public businessUnits: any[];

    // Gets or sets the business transaction type list
    public businessTransactionTypes: any[];

    // Gets or sets the columns for the data table
    public cols = [
        { field: 'receiptId', header: 'Receipt Number' },
        { field: 'businessTransactionNumberId', header: 'Business Transaction Number' },
        { field: 'updatedDate', header: 'Service Date' },
        { field: 'businessUnitName', header: 'Business Unit' },
        { field: 'businessTransactionType', header: 'Service' },
        { field: 'businessTransactionDetail', header: 'Details' },
        { field: 'requestType', header: 'Request Type' },
        { field: 'period', header: 'Period' },
        { field: 'amount', header: 'Amount' },
        { field: 'balance', header: 'Balance' }
    ];

    @ViewChild('dt') customerAccountTransactionsTable: Table;

    private downloadSubscription: Subscription;

    /**
     * constructor
     */
    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private modalService: MvdModalService,
        private accountService: CustomerAccountService,
        private locationService: LocationService,
        private fileSaverService: FileSaverService,
        private receiptService: ReceiptService
    ) {
        super();
    }

    /**
     * Handle the angular ngOnInit event
     */
    public async ngOnInit() {
        this.UseHelpContent('#CONTENT/AZ_MVD_Now/Customer_Account_Service_History.htm%3FTocPath%3DAZ%2520MVD%2520Now%7C_____3');
        this.form = this.formBuilder.group({
            'fromDate': [setServerDate(moment(getCurrentDate()).subtract(30, 'day').toDate()), this.createValidator('dateCannotBeInFuture', input => input > new Date())],
            'toDate': [(getCurrentDate()), this.createValidator('dateCannotBeInFuture', input => input > new Date())],
            'businessUnitId': [null],
            'businessTransactionTypeId': [null],
            'minAmount': [null, Validators.compose([ControlValidationMessagesService.positiveNumber])],
            'maxAmount': [null, Validators.compose([ControlValidationMessagesService.positiveNumber])],
            'receiptId': [null, Validators.compose([ControlValidationMessagesService.positiveNumber])]
        });

        this.formInitialValue = this.form.value;

        var maxAmountLessThanMin = this.createValidator('maxAmountLessThanMin', (input): boolean => {
            if (this.form) {
                var formVal = this.form.value;
                return formVal.maxAmount && formVal.minAmount && formVal.minAmount > formVal.maxAmount;
            }
            return false;
        });

        this.form.setValidators(Validators.compose([maxAmountLessThanMin]));

        if (!this.customerAccountNumberId) {
            this.customerAccountNumberId = +(this.route.snapshot.params['id']);
        }
        const customerAccount = await this.accountService.getCustomerAccountDetails(this.customerAccountNumberId).pipe(first()).toPromise();
        this.customerAccountType = customerAccount.accountType;
        this.getBusinessTransactionTypes();
        await this.getBusinessUnits();
    }

    public exportAll() {
        const fields = ['receiptId', 'businessTransactionNumberId', 'updatedDate', 'businessUnitName', 'customerName', 'businessTransactionType', 'businessTransactionDetail', 'requestType', 'period', 'checkNumber', 'amount', 'balance'];
        const headers = ['Receipt Id', 'Business Transaction Number Id', 'Updated Date', 'Business Unit Name', 'Customer Name', 'Business Transaction Type', 'Detail', 'Request Type', 'Period', 'Check Number', 'Amount', 'Balance'];

        const searchCriteria: CustomerAccountTransactionSearchCriteria = this.form.value;
        searchCriteria.isExport = true;
        this.accountService.searchTransaction(this.customerAccountNumberId, searchCriteria)
            .subscribe({
                next: d => {
                    const result = d.map(this.makeViewRecord);
                    result.map(row => {
                        if (row.type == 'disbursement') {
                            row.receiptId = row.financeDisbursementRequestId;
                        }
                        for (let key of Object.keys(row)) {
                            if (row[key] == null) {
                                row[key] = '';
                            }
                        }
                    });
                    downloadJsonToCsv(result, fields, 'export_service_activity', headers, false);
                },
                error: e => this.handleValidationResult(e)
            });
    }

    /**
     * Handles reset click
     */
    public reset(): void {
        this.searchResults = null;
        this.form.reset(this.formInitialValue);
    }

    /**
     * Reset pagination settings
     */
    public resetPagination(): void {
        this.dataStore = [];
        this.previousPageNumber = -1;
        this.totalRecords = 0;
    }

    /**
     * Handles submit click
     */
    public submit(): void {
        const event: LazyLoadEvent = {
            first: 0,
            rows: this.rows
        };
        this.resetPagination();
        this.populateSearchResults(event);
    }

    private populateSearchResults(event: LazyLoadEvent) {
        if (this.form.valid) {

            const searchCriteria: CustomerAccountTransactionSearchCriteria = this.form.value;
            searchCriteria.lazyLoadEvent = event;
            searchCriteria.isExport = false;

            // Set page load in progress
            this.inProgress = true;

            // Calculate the current page used for determining if we are going to load from the data store or fetch data
            const pageNumber = Math.floor(event.first / this.rows);

            // if all the sorting is the same and we are on a previous page then just reload from the data store
            // otherwise go fetch data
            if (this.previousSort == event.sortField && this.previousOrder == event.sortOrder && this.previousPageNumber >= pageNumber) {
                this.displaySearchResults(event);
            } else {
                this.accountService.searchTransaction(this.customerAccountNumberId, searchCriteria).subscribe({
                    next: d => {

                        // Create our view records
                        var result = d.map(this.makeViewRecord);

                        // Splice these into the data store in case this is a change for sorting
                        // so the existing records will be kept
                        this.dataStore.splice(event.first, event.rows, ...result);

                        // calculate the total records used by the grid if we new data coming in.
                        if (d.length > 0) {
                            this.totalRecords = (pageNumber + 1) * this.rows + 1;
                        }

                        this.displaySearchResults(event);
                    },
                    error: e => {
                        this.searchResults = null;
                        this.inProgress = false;
                        this.handleValidationResult(e);
                    }
                });
            }

            // Set our previous page information
            this.previousPageNumber = pageNumber;
            this.previousSort = event.sortField;
            this.previousOrder = event.sortOrder;
        }
    }

    private displaySearchResults(event: LazyLoadEvent) {

        this.inProgress = false;

        this.searchResults = [];

        // Slice the results and apply the current filter.
        this.searchResults = this.dataStore.slice(event.first, (event.first + event.rows));
        this.searchResults = this.searchResults.filter(row => this.filterGlobal(row, event.globalFilter));
    }

    public filterGlobal(row, value) {
        for (let i = 0; i < this.cols.length; i++) {
            let column: any = this.cols[i];
            if (row[column.field] == null) {
                continue;
            }
            let rowValue: String = row[column.field].toString().toLowerCase();
            if (value == null || rowValue.includes(value.toLowerCase())) {
                return true;
            }
        }
        return false;
    }

    public loadDataLazy(event: LazyLoadEvent) {
        this.populateSearchResults(event);
    }

    /**
     * Creates a validator accepting a lambda.
     */
    protected createValidator(name: string, func: (inputValue: any) => boolean): ValidatorFn {
        return (control: AbstractControl) => {
            if (func(control.value)) {
                return { [name]: true };
            }
            return null;
        };
    }

    /**
     * Wraps a data record for easier binding in the view.
     */
    protected makeViewRecord(item: any): CustomerAccountServiceHistoryViewModel {
        return new CustomerAccountServiceHistoryViewModel(item);
    }

    /**
     * Launches the disbursement summary modal
     * @param financeDisbursementRequestId the finance disbursement request id
     */
    public viewDisbursementRequest(financeDisbursementRequestId: number) {
        const data = new DisbursementSummaryContext(financeDisbursementRequestId);
        this.modalService.open(DisbursementSummaryModal, data);
    }

    /**
     * Download the balance receipt document
     * @param receiptId to download the document
     */
    public checkOutReceipt(receiptId) {
        this.downloadSubscription = this.receiptService.getReceiptDocument(receiptId)
            .pipe(
                filter(document => document && !!document.documentFilename && !!document.documentMimeTypeValue),
                mergeMap(document => !!document.documentContent ?
                    of(document) :
                    throwError(this.createValidationError('This document is not available for download.'))
                ),
                tap(document => this.saveDocumentToFile(document))
            )
            .subscribe({
                error: e => this.handleValidationResult(e)
            });
    }

    private createValidationError(message: string) {
        const validationModel: ValidationModel = new ValidationModel([]);
        const val: ValidationResultModel = new ValidationResultModel();
        const state: ValidationStateModel = new ValidationStateModel();
        state.validationType = 'Error';
        val.errorMessage = message;
        val.source = 'Model';
        val.state = state;
        validationModel.validationResults.push(val);
        return validationModel;
    }

    private saveDocumentToFile(document: any) {
        this.fileSaverService.saveBase64ToFile(document.documentFilename, document.documentContent, document.documentMimeTypeValue);
    }

    /**
     * Gets the business transaction types
     */
    private getBusinessTransactionTypes() {
        const result = this.businessTransactionType.GetActive();
        this.businessTransactionTypes = [];
        for (let businessTransactionType of result) {
            this.businessTransactionTypes.push({ value: businessTransactionType.id.toString(), label: businessTransactionType.displayValue.toString() });
        }

        // Manually insert empty business transaction type to drop down list
        const businessTransactionType = { value: null, label: '' };
        this.businessTransactionTypes.unshift(businessTransactionType);
    }

    /**
     * Gets the current business units.
     */
    private async getBusinessUnits() {
        var result = await this.locationService.getActiveBusinessUnits().toPromise();

        result.result.forEach(businessUnit => {
            businessUnit.value = businessUnit.id;
            businessUnit.label = '(' + businessUnit.businessUnitCode + ') ' + businessUnit.businessUnitName;
        });

        this.businessUnits = result.result;
        // Manually insert empty business unit to drop down list
        const businessUnit = { value: null, label: '' };
        this.businessUnits.unshift(businessUnit);
    }
}
